import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Typography } from "@mui/material";
import * as R from "ramda";
import { useState } from "react";

import { ArtifactType } from "../../../types/artifact";
import { capitalizeFirst } from "../../../utils/pdf";
import { getTimeRangeLabel } from "../../../utils/timerange";
import { ARTIFACT_TYPE_LABELS, ContextFilter } from "../stores/chatTypes";

interface ContextSummaryProps {
  content: string;
  contextFilter: ContextFilter;
}

interface FilterDisplayItem {
  label: string;
  value: string;
}

const FILTER_DISPLAY_NAMES: Record<string, string> = {
  timeRange: "Date range",
  user: "User",
  protocol: "Conversation Lens",
  callType: "Call type",
  sentiment: "Sentiment",
  relevance: "Relevance",
  risk: "Risk indicator",
  processDecision: "Process decision",
  scoreRange: "Score range",
  search: "Search"
};

const PROCESS_DECISION_LABELS: Record<string, string> = {
  DO_PROCESS: "Processed",
  DO_NOT_PROCESS: "Not Processed"
};

const CALL_TYPE_LABELS: Record<string, string> = {
  external: "External",
  internal: "Internal"
};

const FILTER_ORDER: string[] = [
  "timeRange",
  "user",
  "protocol",
  "callType",
  "sentiment",
  "relevance",
  "risk",
  "processDecision",
  "scoreRange",
  "search"
];

const LABEL_MIN_WIDTH = 140;
const MIN_SCORE = 0;
const MAX_SCORE = 5;

const formatFilterValue = (name: string, value: string): string => {
  if (name === "timeRange") {
    return getTimeRangeLabel(value);
  }
  if (name === "processDecision") {
    return PROCESS_DECISION_LABELS[value] ?? value;
  }
  if (name === "callType") {
    return CALL_TYPE_LABELS[value] ?? value;
  }
  if (name === "sentiment" || name === "relevance" || name === "risk") {
    return capitalizeFirst(value);
  }
  return value;
};

const isActiveFilter = (name: string, value: string): boolean => {
  if (name === "timeRange") {
    return true;
  }
  if (name === "search") {
    return value.trim() !== "";
  }
  return value !== "all" && value !== "";
};

const getActiveFilters = (contextFilter: ContextFilter): FilterDisplayItem[] =>
  FILTER_ORDER
    .filter((key) => {
      if (key === "scoreRange") {
        const [minScore, maxScore] = contextFilter.scoreRange;
        return minScore > MIN_SCORE || maxScore < MAX_SCORE;
      }
      const value = contextFilter[key as keyof ContextFilter] as string;
      return isActiveFilter(key, value);
    })
    .map((key) => {
      if (key === "scoreRange") {
        const [minScore, maxScore] = contextFilter.scoreRange;
        return { label: FILTER_DISPLAY_NAMES[key], value: `${minScore} \u2013 ${maxScore}` };
      }
      return {
        label: FILTER_DISPLAY_NAMES[key],
        value: formatFilterValue(key, contextFilter[key as keyof ContextFilter] as string)
      };
    });

const getArtifactTypeLabels = (types: ArtifactType[]): string =>
  R.pipe(
    R.map((type: ArtifactType) => ARTIFACT_TYPE_LABELS[type] ?? type),
    R.join(", ")
  )(types);

const labelSx = {
  color: "primary.main",
  minWidth: LABEL_MIN_WIDTH,
  flexShrink: 0
} as const;

const valueSx = {
  color: "text.secondary"
} as const;

const sectionHeaderSx = {
  fontWeight: 700,
  letterSpacing: 0.5,
  display: "block",
  mb: 0.5,
  color: "text.primary"
} as const;

interface ExpandedDetailsProps {
  artifactTypeLabels: string;
  totalTokens: number;
  activeFilters: FilterDisplayItem[];
}

const ExpandedDetails = ({ artifactTypeLabels, totalTokens, activeFilters }: ExpandedDetailsProps): React.ReactElement => (
  <Box sx={{ mt: 1 }}>
    <Typography variant="caption" sx={sectionHeaderSx}>
      DATASET
    </Typography>
    <Box sx={{ display: "flex", gap: 2, pl: 1, mb: 1 }}>
      <Typography variant="body2" sx={labelSx}>
        {artifactTypeLabels}
      </Typography>
      <Typography variant="body2" sx={valueSx}>
        {totalTokens.toLocaleString()} tokens
      </Typography>
    </Box>

    <Typography variant="caption" sx={sectionHeaderSx}>
      FILTERS
    </Typography>
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      pl: 1
    }}>
      {activeFilters.map((filter) => (
        <Box key={filter.label} sx={{ display: "flex", gap: 2 }}>
          <Typography variant="body2" sx={labelSx}>
            {filter.label}
          </Typography>
          <Typography variant="body2" sx={valueSx}>
            {filter.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const ContextSummary = ({ content, contextFilter }: ContextSummaryProps): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);
  const activeFilters = getActiveFilters(contextFilter);
  const artifactTypeLabels = getArtifactTypeLabels(contextFilter.includedArtifactTypes);

  return (
    <Box>
      <Box
        onClick={() => setIsExpanded((prev) => !prev)}
        sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
      >
        <Typography variant="body2" sx={{ flex: 1, whiteSpace: "pre-line", color: "inherit" }}>
          {content}
        </Typography>
        <Box sx={{ ml: 1, mt: -0.5, color: "inherit", display: "flex", alignItems: "center", p: 0.5 }}>
          {isExpanded
            ? <ExpandLessIcon fontSize="small" />
            : <ExpandMoreIcon fontSize="small" />
          }
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <ExpandedDetails
          artifactTypeLabels={artifactTypeLabels}
          totalTokens={contextFilter.totalTokens}
          activeFilters={activeFilters}
        />
      </Collapse>
    </Box>
  );
};

export default ContextSummary;
